import React, { useEffect, useRef, useState } from "react";

import clsx from "clsx";
import Image from "next/image";

import BanksCreditUnionsIcon from "public/images/icons/solar-financing-operating-system/Banks-credit-unions.svg";
import CapitalProvidersIcon from "public/images/icons/solar-financing-operating-system/Capital-providers.svg";
import SolarLendersIcon from "public/images/icons/solar-financing-operating-system/Solar-lenders.svg";
import TpoFinanciersIcon from "public/images/icons/solar-financing-operating-system/TPO-financiers.svg";

import Heading from "components/heading";

import HeroSection from "../../hero-section";

const features = [
  {
    title: "Banks & Credit Unions",
    description: "Launch solar loan programs with compliant, white-labeled origination infrastructure.",
    icon: BanksCreditUnionsIcon,
  },
  {
    title: "Capital Providers",
    description: "Deploy capital into residential solar with full portfolio visibility from day one.",
    icon: CapitalProvidersIcon,
  },
  {
    title: "Solar Lenders",
    description: "Run your entire lending program — origination, decisioning, and servicing — under your brand.",
    icon: SolarLendersIcon,
  },
  {
    title: "TPO Financiers",
    description: "Originate and manage solar leases, PPAs, and pre-paid PPAs through a nationwide installer network.",
    icon: TpoFinanciersIcon,
  },
];

const FeatureBlock = ({
  title,
  description,
  icon,
  isActive,
  ...rest
}: typeof features[number] & { isActive: boolean } & React.HTMLAttributes<HTMLLIElement>) => (
  <li
    {...rest}
    className={clsx(
      "flex-1 flex flex-col sm:flex-row gap-4 items-center p-4 rounded-2xl border-2 text-center sm:text-left transition-all duration-1000",
      isActive
        ? "bg-navy shadow-[0_0_10px_0] border-primary shadow-primary"
        : "border-gray-500 bg-gray-900/50 opacity-80"
    )}
  >
    <figure className="h-12 w-12 shrink-0 p-1.5 rounded-lg border border-primary flex items-center justify-center bg-primary/10">
      <Image src={icon} alt={`${title} - Icon`} />
    </figure>
    <div className="space-y-1.5">
      <Heading tag="h4" size="h6" className="text-white">
        {title}
      </Heading>
      <p className={clsx("transition-colors duration-1000", isActive ? "text-gray-200" : "text-gray-300")}>
        {description}
      </p>
    </div>
  </li>
);

const Features = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const isPaused = useRef(false);

  useEffect(() => {
    const interval = setInterval(() => {
      if (!isPaused.current) {
        setActiveIndex(i => (i + 1) % features.length);
      }
    }, 3000);

    return () => {
      clearInterval(interval);
    };
  }, []);

  return (
    <HeroSection.Content className="w-full flex flex-col gap-4 sm:px-6 md:text-center lg:w-1/2 lg:px-10">
      <HeroSection.Tagline className="text-primary lg:text-left">Who We Serve</HeroSection.Tagline>
      <ul className="grid gap-3 grid-cols-1 md:grid-cols-2 lg:grid-cols-1">
        {features.map((feature, i) => (
          <FeatureBlock
            key={feature.title}
            {...feature}
            isActive={i === activeIndex}
            onMouseEnter={() => {
              isPaused.current = true;
              setActiveIndex(i);
            }}
            onMouseLeave={() => {
              isPaused.current = false;
            }}
          />
        ))}
      </ul>
    </HeroSection.Content>
  );
};

const title = "For Residential Solar Financiers";

const MainHero = () => (
  <div className="bg-navy bg-gradient-to-br from-navy-dark via-navy to-gray-700 text-white py-10">
    <HeroSection title={title} className="flex-col gap-x-6 gap-y-14 lg:flex-row md:space-x-0 lg:items-start">
      <HeroSection.Content className="w-full sm:px-6 lg:w-1/2 lg:px-10 md:text-center lg:text-left">
        <HeroSection.Tagline className="text-primary mb-4">{title}</HeroSection.Tagline>
        <HeroSection.Header className="lg:items-start">
          <Heading tag="h1" size="h2" className="text-white">
            <span>Your Operating System for Financing Residential Solar.</span>
            <br />
            <span className="text-primary inline-block my-1">Zero Build.</span>
          </Heading>
        </HeroSection.Header>
        <HeroSection.Description className="text-gray-100 max-w-3xl mx-auto">
          Built for solar financiers launching new programs and those ready to replace the clunky, limited
          infrastructure that’s been holding them back. Banks, credit unions, capital providers, solar lenders, and TPO
          financiers use Enerflo to originate, oversee risk, and manage operations across solar loans, PPAs, pre-paid
          PPAs, and leases. Enerflo is the solar financing operating system built to move faster, scale confidently, and
          keep their brand out front.
        </HeroSection.Description>
        <nav className="flex flex-col gap-4 mt-8 xs:flex-row xs:justify-center lg:justify-start">
          <a
            href="#get-started"
            className="btn bg-primary border-2 border-transparent text-white px-6 py-3 text-center"
          >
            Book a Discovery Call
          </a>
          <a
            href="#the-solar-financing-operating-system"
            className="btn bg-transparent border-2 border-white text-white px-6 py-3 text-center"
          >
            See How It Works
          </a>
        </nav>
      </HeroSection.Content>

      <Features />
    </HeroSection>
  </div>
);

export default MainHero;
